/* eslint-disable no-case-declarations */

import { randomNumber } from '../../../utilities'
import { getCodedValue, getUnitIdValue } from '../utils/sceneDescription.util'
import { SceneDescriptionConstants } from './sceneDescription.constants'
import { IOtherAgency, ISceneDescriptionAction, ISceneDescriptionState } from './sceneDescription.types'

export const initialOtherAgency = (): IOtherAgency => {
  return {
    key: randomNumber().toString(),
    agencyName: null,
    agencyType: null,
    agencyNumber: null,
    firstOnScene: null,
    firstOnScenePNNV: null,
    coded: null,
    unitId: null,
  }
}

export const initialSceneDescriptionState: ISceneDescriptionState = {
  sceneDescription: '',
  numPtsScene: null,
  firstOnScene: null,
  firstOnScenePNNV: null,
  levelCarePerProtocol: null,
  levelCarePerProtocolPNNV: null,
  prOtherAgencies: [],
  transferOfCare: null,
  transferOfCarePNNV: null,
  otherAgencies: [],
  lawnum: '',
  firenum: '',
  prDisaster: [],
  prDispoInstructions: [],
  belongings: '',
  prPtMovedVia: [],
  prPtPosition: [],
  prPtMovedFrom: [],
  prTransportAssess: [],
  stretcherPurposeDescr: '',
  cadComments: '',
  showFirstOnSceneMessage: false,
  firstOnSceneKey: null,
  firstOnScenePreviousValue: null,
}

export function sceneDescriptionReducer(
  state: ISceneDescriptionState = initialSceneDescriptionState,
  action: ISceneDescriptionAction
): ISceneDescriptionState {
  switch (action.type) {
    case SceneDescriptionConstants.SET_SCENE_DESCRIPTION:
      return { ...state, sceneDescription: action.sceneDescription }
    case SceneDescriptionConstants.SET_NUM_PTS_SCENE:
      return { ...state, numPtsScene: action.numPtsScene }
    case SceneDescriptionConstants.SET_FIRST_ON_SCENE:
      return { ...state, firstOnScene: action.firstOnScene }
    case SceneDescriptionConstants.SET_FIRST_ON_SCENE_PNNV:
      return { ...state, firstOnScenePNNV: action.firstOnScenePNNV }
    case SceneDescriptionConstants.SET_LEVEL_CARE_PER_PROTOCOL:
      return { ...state, levelCarePerProtocol: action.levelCarePerProtocol }
    case SceneDescriptionConstants.SET_LEVEL_CARE_PER_PROTOCOL_PNNV:
      return { ...state, levelCarePerProtocolPNNV: action.levelCarePerProtocolPNNV }
    case SceneDescriptionConstants.SET_PR_OTHER_AGENCIES:
      return { ...state, prOtherAgencies: action.prOtherAgencies }
    case SceneDescriptionConstants.SET_TRANSFER_OF_CARE:
      return { ...state, transferOfCare: action.transferOfCare }
    case SceneDescriptionConstants.SET_TRANSFER_OF_CARE_PNNV:
      return { ...state, transferOfCarePNNV: action.transferOfCarePNNV }
    case SceneDescriptionConstants.SET_LAWNUM:
      return { ...state, lawnum: action.lawnum }
    case SceneDescriptionConstants.SET_FIRENUM:
      return { ...state, firenum: action.firenum }
    case SceneDescriptionConstants.SET_PR_DISASTER:
      return { ...state, prDisaster: action.prDisaster }
    case SceneDescriptionConstants.SET_PR_DISPO_INSTRUCTIONS:
      return { ...state, prDispoInstructions: action.prDispoInstructions }
    case SceneDescriptionConstants.SET_BELONGINGS:
      return { ...state, belongings: action.belongings }
    case SceneDescriptionConstants.SET_PR_PT_MOVED_VIA:
      return { ...state, prPtMovedVia: action.prPtMovedVia }
    case SceneDescriptionConstants.SET_PR_PT_POSITION:
      return { ...state, prPtPosition: action.prPtPosition }
    case SceneDescriptionConstants.SET_PR_PT_MOVED_FROM:
      return { ...state, prPtMovedFrom: action.prPtMovedFrom }
    case SceneDescriptionConstants.SET_PR_TRANSPORT_ASSESS:
      return { ...state, prTransportAssess: action.prTransportAssess }
    case SceneDescriptionConstants.SET_STRETCHER_PURPOSE_DESCR:
      return { ...state, stretcherPurposeDescr: action.stretcherPurposeDescr }
    case SceneDescriptionConstants.SET_CAD_COMMENTS:
      return { ...state, cadComments: action.cadComments }
    case SceneDescriptionConstants.DELETE_CAD_COMMENTS:
      return { ...state, cadComments: '' }
    case SceneDescriptionConstants.ADD_NEW_AGENCY:
      return {
        ...state,
        otherAgencies: [...state.otherAgencies, { ...initialOtherAgency() }],
      }
    case SceneDescriptionConstants.REMOVE_AGENCY:
      return {
        ...state,
        otherAgencies: state.otherAgencies.filter((otherAgency) => {
          return otherAgency.key !== action.key
        }),
      }
    case SceneDescriptionConstants.SET_OTHER_AGENCIES:
      return {
        ...state,
        otherAgencies: action.otherAgencies,
      }
    case SceneDescriptionConstants.SET_OTHER_AGENCY_NAME:
      return {
        ...state,
        otherAgencies: state.otherAgencies.map((otherAgency) => {
          if (otherAgency.key === action.key) {
            return {
              ...otherAgency,
              agencyName: action.agencyName,
              coded: getCodedValue(action.agencyName),
              unitId: getUnitIdValue(action.agencyName),
            }
          }
          return otherAgency
        }),
      }
    case SceneDescriptionConstants.SET_OTHER_AGENCY_TYPE:
      return {
        ...state,
        otherAgencies: state.otherAgencies.map((otherAgency) => {
          if (otherAgency.key === action.key) {
            return {
              ...otherAgency,
              agencyType: action.agencyType,
            }
          }
          return otherAgency
        }),
      }
    case SceneDescriptionConstants.SET_OTHER_AGENCY_NUMBER:
      return {
        ...state,
        otherAgencies: state.otherAgencies.map((otherAgency) => {
          if (otherAgency.key === action.key) {
            return {
              ...otherAgency,
              agencyNumber: action.agencyNumber,
            }
          }
          return otherAgency
        }),
      }
    case SceneDescriptionConstants.SET_OTHER_AGENCY_FIRST_ON_SCENE:
      const isFirstOnSceneAlreadyTaken = state.otherAgencies.some(
        (otherAgency) =>
          action.firstOnScene?.name === 'Yes' &&
          otherAgency.key !== action.key &&
          otherAgency.firstOnScene?.name === 'Yes'
      )
      let previousValue = null

      const newOtherAgencies = state.otherAgencies.map((otherAgency) => {
        if (otherAgency.key === action.key) {
          previousValue = otherAgency.firstOnScene || otherAgency.firstOnScenePNNV

          return {
            ...otherAgency,
            firstOnScene: action.firstOnScene,
          }
        }
        return otherAgency
      })

      return {
        ...state,
        showFirstOnSceneMessage: isFirstOnSceneAlreadyTaken,
        firstOnSceneKey: isFirstOnSceneAlreadyTaken ? action.key : null,
        firstOnScenePreviousValue: previousValue,
        otherAgencies: newOtherAgencies,
      }
    case SceneDescriptionConstants.SET_OTHER_AGENCY_FIRST_ON_SCENE_PNNV:
      return {
        ...state,
        otherAgencies: state.otherAgencies.map((otherAgency) => {
          if (otherAgency.key === action.key) {
            return {
              ...otherAgency,
              firstOnScenePNNV: action.firstOnScenePNNV,
            }
          }
          return otherAgency
        }),
      }
    case SceneDescriptionConstants.CLOSE_FIRST_ON_SCENE_MESSAGE:
      return {
        ...state,
        showFirstOnSceneMessage: false,
        firstOnSceneKey: null,
      }
    case SceneDescriptionConstants.SET_SCENE_DESCRIPTION_STATE:
      return { ...action.sceneDescriptionState }
    case SceneDescriptionConstants.RESET_SCENE_DESCRIPTION_STATE:
      return { ...initialSceneDescriptionState }
    default:
      return state
  }
}
